/*
 * Copyright (c) 2026 Bluemate B.V.
 */

import {RefObject, useEffect, useRef} from 'react';

interface Options {
    /** Grows the observed area so an element is reported slightly before it
     *  scrolls into view — enrichment for the next cards is in flight by the
     *  time they appear. */
    rootMargin?: string;
    /** When this value changes the element is observed afresh. Pass the model
     *  instance a card renders: a plain re-render (e.g. enrichment merge that
     *  mutates the same instance) keeps identity and does NOT re-fire, while a
     *  list reload swaps in a new instance and re-arms the observer. */
    resetKey?: unknown;
}

/**
 * Fires `onInView` exactly once, the first time `ref`'s element intersects the
 * viewport (plus `rootMargin`), then stops observing. Re-arms when `resetKey`
 * changes. Where IntersectionObserver is unavailable (happy-dom tests, very old
 * browsers) it fires immediately so callers still receive their data.
 */
export function useInView(ref: RefObject<Element | null>, onInView: () => void, options: Options = {}) {
    const {rootMargin = '300px', resetKey} = options;
    // Keep the latest callback without re-subscribing the observer every render.
    const callbackRef = useRef(onInView);
    callbackRef.current = onInView;

    useEffect(() => {
        const element = ref.current;
        if (!element) return;
        if (typeof IntersectionObserver === 'undefined') {
            callbackRef.current();
            return;
        }
        const observer = new IntersectionObserver(
            entries => {
                if (entries.some(entry => entry.isIntersecting)) {
                    callbackRef.current();
                    observer.disconnect();
                }
            },
            {rootMargin}
        );
        observer.observe(element);
        return () => observer.disconnect();
        // callbackRef is a stable ref; resetKey re-arms on identity change.
    }, [ref, rootMargin, resetKey]);
}
